import React, { useState, useEffect, useRef } from 'react';
import { Layout, Menu, Typography, Button, message, Tooltip } from 'antd';
import {
    UserOutlined,
    LogoutOutlined,
    HomeOutlined,
    SearchOutlined,
    DollarOutlined,
    WarningOutlined,
    CheckSquareOutlined,
    RocketOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';
import './LayoutMobile.css';

const { Header, Content, Sider } = Layout;
const { Title } = Typography;

const REQUIRED_PROFILE_FIELDS = [
    'identification', 'full_name', 'date_of_birth', 'contact_address',
    'province_id', 'commune_id', 'student_phone_number', 'parents_phone_number',
    'graduation_year', 'grade_10_rank', 'grade_11_rank', 'grade_12_rank',
    'first_admission_id', 'admission_area',
];

function checkProfileComplete(profile) {
    if (!profile) return false;
    return REQUIRED_PROFILE_FIELDS.every(field => {
        const val = profile[field];
        return val !== null && val !== undefined && val !== '';
    });
}

const UserLayout = ({ children, title }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
    const [profileComplete, setProfileComplete] = useState(true);
    const menuBarRef = useRef(null);

    useEffect(() => {
        const userData = localStorage.getItem('user');
        if (userData) {
            setUser(JSON.parse(userData));
        }
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 992);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        axiosInstance.get('/api/v1/student-profile')
            .then(res => {
                if (res.data?.success) {
                    setProfileComplete(checkProfileComplete(res.data.data));
                }
            })
            .catch(() => {
                // If profile fetch fails, keep default (don't block UI)
            });
    }, [location.pathname]);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');
        message.success('Đăng xuất thành công!');
        navigate('/login');
    };

    const paymentLabel = profileComplete ? 'Nộp lệ phí' : (
        <Tooltip title="Vui lòng hoàn thiện thông tin hồ sơ dự tuyển trước khi nộp lệ phí">
            <span>
                Nộp lệ phí <WarningOutlined style={{ color: '#faad14', marginLeft: 4 }} />
            </span>
        </Tooltip>
    );

    const menuItems = [
        {
            key: 'home',
            icon: <HomeOutlined />,
            label: 'Trang chủ',
            onClick: () => navigate('/')
        },
        {
            key: 'profile',
            icon: <UserOutlined />,
            label: 'Hồ sơ dự tuyển',
            onClick: () => navigate('/student-profile')
        },
        {
            key: 'checklist',
            icon: <CheckSquareOutlined />,
            label: 'Trạng thái hồ sơ',
            onClick: () => navigate('/checklist')
        },
        {
            key: 'payment',
            icon: <DollarOutlined />,
            label: paymentLabel,
            disabled: !profileComplete,
            onClick: profileComplete ? () => navigate('/payment') : undefined
        },
        {
            key: 'result-lookup',
            icon: <SearchOutlined />,
            label: 'Tra cứu kết quả',
            onClick: () => navigate('/result-lookup')
        },
        {
            key: 'admission-evaluator',
            icon: <RocketOutlined />,
            label: 'Đánh giá cơ hội',
            onClick: () => navigate('/admission-evaluator')
        }
    ];

    const currentMenuKey =
        location.pathname === '/student-profile'      ? 'profile' :
        location.pathname === '/checklist'            ? 'checklist' :
        location.pathname === '/payment'              ? 'payment' :
        location.pathname === '/result-lookup'        ? 'result-lookup' :
        location.pathname === '/admission-evaluator'  ? 'admission-evaluator' : 'home';

    // Auto-scroll selected menu item into view
    useEffect(() => {
        if (isMobile && menuBarRef.current) {
            const selectedItem = menuBarRef.current.querySelector('.ant-menu-item-selected');
            if (selectedItem) {
                selectedItem.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'center'
                });
            }
        }
    }, [currentMenuKey, isMobile]);

    return (
        <Layout style={{ minHeight: '100vh' }}>
            <Sider
                breakpoint="lg"
                collapsedWidth="0"
            >
                <div style={{ height: 32, margin: 16, background: 'rgba(255, 255, 255, 0.2)' }} />
                <Menu
                    theme="dark"
                    mode="inline"
                    selectedKeys={[currentMenuKey]}
                    items={menuItems}
                />
            </Sider>
            <Layout>
                <Header className="layout-header" style={{ padding: 0, background: '#fff' }}>
                    {/* Top bar: Title + User + Logout */}
                    <div className="header-top-bar">
                        <Title level={2} className="header-title" style={{ margin: 0 }}>
                            {title || 'HỆ THỐNG ĐĂNG KÝ DỰ TUYỂN'}
                        </Title>
                        <div className="header-user-section">
                            <span className="header-user-name">
                                <UserOutlined /> {user?.name || user?.email}
                            </span>
                            <Button
                                type="primary"
                                icon={<LogoutOutlined />}
                                onClick={handleLogout}
                                className="header-logout-btn"
                            >
                                Đăng xuất
                            </Button>
                        </div>
                    </div>

                    {/* Mobile horizontal menu */}
                    {isMobile && (
                        <div className="mobile-menu-bar" ref={menuBarRef}>
                            <Menu
                                mode="horizontal"
                                selectedKeys={[currentMenuKey]}
                                items={menuItems}
                            />
                        </div>
                    )}
                </Header>
                <Content style={{ margin: '24px 16px 0' }}>
                    <div style={{ padding: 24, minHeight: 360, background: '#fff' }}>
                        {children}
                    </div>
                </Content>
            </Layout>
        </Layout>
    );
};

export default UserLayout;
